<template>
  <div>
    <div class="text-right mb-3">
      <v-btn
        text
        color="primary"
        :disabled="customProperties.models[0] && customProperties.models[0].isNew()"
        @click="newCustomProp"
      >
        <v-icon left>
          add
        </v-icon>
        {{ $t('create') }}
      </v-btn>
    </div>

    <v-expansion-panels v-model="expandedPanel">
      <v-expansion-panel
        v-for="customProp in customProperties.models"
        :key="customProp.id"
      >
        <v-expansion-panel-header>
          <div style="display:flex;">
            <span>{{ customProp.name }}</span>
            <span
              v-show="customProp.description"
              class="grey--text ml-2"
            >
              {{ customProp.description }}
            </span>

            <v-spacer />

            <v-chip
              v-for="att in customProp.attachedTo"
              :key="att"
              small
              color="primary"
              outlined
              class="mr-3"
            >
              {{ (attachedToValues.find(x => x.value === att) || {}).text }}
            </v-chip>
          </div>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <v-form v-model="validForm">
            <v-container>
              <v-layout
                row
                wrap
              >
                <v-flex
                  xs12
                  sm6
                  md4
                  lg3
                >
                  <v-text-field
                    v-model="customProp.name"
                    :label="$t('name')"
                    @input="onNameChange(customProp)"
                  />
                </v-flex>

                <v-flex
                  xs12
                  sm6
                  md4
                  lg3
                >
                  <v-text-field
                    v-model="customProp.description"
                    :label="$t('description')"
                  />
                </v-flex>

                <v-flex
                  xs12
                  sm6
                  md4
                  lg3
                >
                  <v-select
                    v-model="customProp.attachedTo"
                    label="Attached to"
                    :items="attachedToValues"
                    multiple
                    hint="Where the field should appear"
                    persistent-hint
                  />
                </v-flex>

                <v-flex
                  xs12
                  sm6
                  md4
                  lg3
                >
                  <v-text-field
                    v-model="customProp.key"
                    label="Key"
                    hint="Used on the database to store the value"
                    persistent-hint
                    :rules="[ ruleValidKey(customProp) ]"
                  />
                </v-flex>

                <v-flex
                  xs12
                  sm6
                  md4
                  lg3
                >
                  <v-select
                    v-model="customProp.type"
                    label="Type"
                    :items="typeValues"
                  />
                </v-flex>

                <v-flex
                  xs12
                  sm6
                  md4
                  lg3
                >
                  <v-combobox
                    v-model="customProp.values"
                    label="List values"
                    :hint="customProp.type !== 'enum' ? 'Only for type \'Value from list\'' : 'Type a value and hit tab or enter'"
                    :disabled="customProp.type !== 'enum'"
                    persistent-hint
                    multiple
                    chips
                  />
                </v-flex>

                <v-flex xs12>
                  <v-divider />

                  <p class="text-center mt-3">
                    This is how the field will look like
                  </p>

                  <pa-custom-property-field :custom-prop="customProp" />
                </v-flex>

                <v-flex xs12>
                  <v-btn
                    v-t="'save'"
                    color="success"
                    :disabled="customProp.saving || customProp.loading || !validForm"
                    :loading="customProp.saving"
                    @click="saveCustomProperty(customProp)"
                  />
                </v-flex>
              </v-layout>
            </v-container>
          </v-form>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import Activity from '@/models/Activity'
import CustomFields from '@/models/CustomFields'
import CustomField from '@/models/CustomField'
import Expense from '@/models/Expense'
import Project from '@/models/Project'
import Resource from '@/models/Resource'
import User from '@/models/User'
import PaCustomPropertyField from '@/components/shared/PaCustomPropertyField'

const RE_VALID_KEY = /(^_+)|(_+$)|\.|\/|([^\x20-\x7E]+)/g

export default {
  name: 'CustomFields',
  components: {
    PaCustomPropertyField
  },
  data () {
    return {
      attachedToValues: [
        { text: 'Activity', value: 'activity', model: Activity },
        { text: 'Expense', value: 'expense', model: Expense },
        { text: 'Project', value: 'project', model: Project },
        { text: 'Resource', value: 'resource', model: Resource },
        { text: 'User', value: 'user', model: User }
      ],
      typeValues: [
        { text: 'Text', value: 'string' },
        { text: 'Number', value: 'number' },
        { text: 'True/False', value: 'boolean' },
        { text: 'Value from list', value: 'enum' }
      ],
      customProperties: new CustomFields(),
      expandedPanel: null,
      validForm: false
    }
  },
  mounted () {
    this.customProperties.fetch({
      organization: this.$currentOrganization.id
    })
  },
  methods: {
    async saveCustomProperty (customField) {
      await customField.save()
      this.$store.commit('atlas/syncModel', {
        path: 'organizations.customFields',
        value: this.customProperties.models.map(x => { return { ...x.$ } })
      })
    },
    getKeyBlacklist (prop) {
      let blacklist = []

      for (const att of prop.attachedTo) {
        const item = this.attachedToValues.find(a => a.value === att)

        blacklist = blacklist.concat(
          // eslint-disable-next-line new-cap
          Object.keys(new item.model().attributes).filter(k => k !== prop.key),
          this.customProperties
            .filter(p => (p.id !== prop.id) && (p.attachedTo.includes(att)))
            .map(p => p.key)
        )
      }

      return blacklist
    },
    ruleValidKey (customProp) {
      return (val) => {
        if (!customProp.attachedTo.length) {
          return 'Please select an Attached To value'
        }

        if (customProp.key.match(RE_VALID_KEY)) {
          return 'Please don\'t use . / trailing _ and non-printable chars'
        }

        return !this.getKeyBlacklist(customProp).includes(val) ||
          'Key already in use'
      }
    },
    newCustomProp () {
      this.customProperties.models.unshift(new CustomField())
      this.$nextTick(() => {
        // Workaround for vuetify bug
        this.expandedPanel = this.customProperties.length - 1
      })
    },
    onNameChange (prop) {
      // don't mess up
      if (prop.id) { return }
      /**
        *  Must be valid UTF-8 characters
        *  Must be no longer than 1,500 bytes
        *  Cannot contain a forward slash (/)
        *  Cannot solely consist of a single period (.) or double periods (..)
        *  Cannot match the regular expression __.*__
       */
      prop.key = prop.name.replace(RE_VALID_KEY, '')
    }
  }
}
</script>

<style>

</style>
